import { Box, Chip, ChipOwnProps, ChipPropsColorOverrides, IconButton, Paper, Typography } from "@mui/material"
import { Button, Layout, Modal, Table } from "../../components"
import { Background } from "./styles"
import { useMilespayAPI, useToggle } from "../../hooks"
import { CreateOrderModal } from "./CreateOrderModal"
import { formatDateTime, formatMoney, GATEWAY_PROVIDER_OPTIONS, COMPANY_ORDER_STATUS_OPTIONS } from "@milespay.co/utils"
import { company_customer, company_gateway, COMPANY_ORDER_STATUS, Prisma } from "@milespay.co/db"
import { ContentCopy, CopyAll } from "@mui/icons-material"
import { enqueueSnackbar } from "notistack"
import { useMemo, useState } from "react"

export const HomePage = () => {
    const [orders, ordersControls] = useMilespayAPI('/company-order')
    const [createOrderModalVisibility, createOrderModalVisibilityControls] = useToggle(false)

    const [cancelOrderModalVisibility, cancelOrderModalVisibilityControls] = useToggle(false)
    const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null)

    const [{ loading: cancelLoading }, cancelOrder] = useMilespayAPI({
        url: selectedOrderId ? `/company-order/${selectedOrderId}/cancel` : '/company-order/_/cancel',
        method: 'PATCH'
    }, { manual: true })
    
    const selectedOrder = useMemo(() => {
        if (!selectedOrderId) return null
        return (orders.data || []).find((o: Prisma.company_orderUncheckedCreateInput & { status: COMPANY_ORDER_STATUS }) => o.id === selectedOrderId) || null
    }, [orders.data, selectedOrderId])
    
    return (
        <>
        <Layout>
            <Box>
                <Box display={"flex"} justifyContent={"flex-end"} mb={2}>
                    <Button onClick={createOrderModalVisibilityControls.setTrue}>
                        Adicionar
                    </Button>
                </Box>
                <Box component={Paper} width={"90vw"} maxHeight={"600px"} overflow={"auto"}>
                    <Table 
                        columns={[
                            { field: 'id', headerName: 'ID', valueFormatter: (value: string) => value.split("-")[0] },
                            { field: 'locator', headerName: 'Localizador', flex: 1, valueFormatter: (value: string | null) => value || '—' },
                            { field: 'created_at', headerName: 'Data/Hora', width: 170, valueFormatter: formatDateTime},
                            { field: 'customer', headerName: 'Cliente', flex: 1, valueGetter: (row: company_customer) => row.name },
                            { field: 'price', headerName: 'Valor', flex: 1, valueFormatter: formatMoney },
                            { field: 'installments', headerName: 'Parcelas Permitidas', flex: 1, valueFormatter: (value) => `${value}x` },
                            { field: 'payment_link', headerName: 'Link', flex: 1, renderCell: ({value}) => {
                                return (
                                    <div style={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        <IconButton
                                            onClick={() => {
                                                window.navigator.clipboard.writeText(value)
                                                enqueueSnackbar('Link copiado!', {variant: 'success'})
                                            }}
                                        >
                                            <ContentCopy />
                                        </IconButton>
                                        <a href={value} target="_blank" rel="noreferrer">
                                            {value}
                                        </a>
                                    </div>
                                )
                            } },
                            { field: 'company_gateway', headerName: 'Gateway', flex: 1, valueGetter: (row: company_gateway) => {
                                return GATEWAY_PROVIDER_OPTIONS.find(item => item.value === row.gateway_provider)?.label
                            } },
                            { field: 'status', headerName: 'Status', width: 150, valueGetter: (value) => {
                                return COMPANY_ORDER_STATUS_OPTIONS.find(item => item.value === value)?.label
                            }, renderCell: ({value, row}) => {
                                const color =  COMPANY_ORDER_STATUS_OPTIONS.find(item => item.value === row.status)?.chipColor
                                
                                return (
                                    <Chip label={value} color={color as ChipOwnProps['color']} />
                                )
                            } },
                            { field: 'actions', headerName: 'Ações', width: 140, sortable: false, filterable: false, renderCell: ({ row }) => {
                                const canCancel = row.status === COMPANY_ORDER_STATUS.PENDING

                                return (
                                    <Button
                                        disabled={!canCancel}
                                        variant="text"
                                        onClick={() => {
                                            setSelectedOrderId(row.id)
                                            cancelOrderModalVisibilityControls.setTrue()
                                        }}
                                    >
                                        Cancelar
                                    </Button>
                                )
                            }},
                        ]}
                        rows={orders.data || []}
                    />
                </Box>
            </Box>
        </Layout>
        {createOrderModalVisibility && (
            <CreateOrderModal 
                onClose={createOrderModalVisibilityControls.setFalse}
                onSuccess={() => {
                    createOrderModalVisibilityControls.setFalse()
                    ordersControls()
                }}
            />
        )}
        {cancelOrderModalVisibility && (
            <Modal
                open
                title="Cancelar link de pagamento?"
                onClose={cancelLoading ? undefined : cancelOrderModalVisibilityControls.setFalse}
                onCancel={cancelLoading ? undefined : () => {
                    cancelOrderModalVisibilityControls.setFalse()
                    setSelectedOrderId(null)
                }}
                cancelLabel="Voltar"
                onConfirm={cancelLoading || !selectedOrderId ? undefined : async () => {
                    try {
                        const response = await cancelOrder()
                        if (response.status >= 200 && response.status < 300) {
                            enqueueSnackbar('Link cancelado com sucesso!', { variant: 'success' })
                            cancelOrderModalVisibilityControls.setFalse()
                            setSelectedOrderId(null)
                            ordersControls()
                        }
                    } catch (e) {
                        enqueueSnackbar('Não foi possível cancelar o link. Tente novamente.', { variant: 'error' })
                    }
                }}
                confirmLabel={cancelLoading ? 'Cancelando...' : 'Confirmar cancelamento'}
                bodyCustomCss={{ width: 520 }}
            >
                <Box>
                    <Typography>
                        Você tem certeza que deseja cancelar este link de pagamento?
                    </Typography>
                    <Typography variant="caption" display="block" mt={1}>
                        {selectedOrderId ? `ID: ${selectedOrderId}` : null}
                        {selectedOrder?.locator ? ` • Localizador: ${selectedOrder.locator}` : null}
                        {selectedOrder?.status ? ` • Status atual: ${COMPANY_ORDER_STATUS_OPTIONS.find(o => o.value === selectedOrder.status)?.label}` : null}
                    </Typography>
                </Box>
            </Modal>
        )}
        </>
    )
}